import { useState } from 'react';
import Logo from '../../components/base/Logo';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo />
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="#home" className="text-gray-900 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Home</a>
                <a href="#services" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Services</a>
                <a href="#inspection-scope" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Inspection Scope</a>
                <a href="#about" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">About</a>
                <a href="#contact" className="text-gray-700 hover:text-teal-600 px-3 py-2 text-sm font-medium cursor-pointer">Contact</a>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <a href="tel:9514234096" className="flex items-center space-x-2 text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <span className="font-semibold">(951) 423-4096</span>
              </a>
              <button onClick={scrollToContact} className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Schedule Inspection
              </button>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-gray-900 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-menu-line text-xl"></i>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-100">
              <a href="tel:9514234096" className="flex items-center space-x-2 px-3 py-2 text-gray-700 font-semibold cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <span>(951) 423-4096</span>
              </a>
              <a href="#home" className="text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Home</a>
              <a href="#services" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Services</a>
              <a href="#inspection-scope" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Inspection Scope</a>
              <a href="#about" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">About</a>
              <a href="#contact" className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-base font-medium cursor-pointer">Contact</a>
              <button onClick={scrollToContact} className="w-full text-left bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Schedule Inspection
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-20 lg:py-32">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
             style={{
               backgroundImage: `url('https://public.readdy.ai/ai/img_res/28cbca56d9dfc8e898d560e75dff1502.jpg')`
             }}>
          <div className="absolute inset-0 bg-white/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Professional Home Inspections in Southern California
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Licensed and certified home inspectors providing comprehensive property evaluations. We follow CREIA and InterNACHI standards to ensure your investment is protected.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={scrollToContact} className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  Schedule Your Inspection
                </button>
                <button className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer">
                  View Sample Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection Scope Section */}
      <section id="inspection-scope" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              🏠 Complete Inspection Scope
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our licensed inspectors examine all visible and accessible components of your property following California state and industry standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-4-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Roofing System</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Condition of shingles and tiles</li>
                <li>• Flashing and waterproofing</li>
                <li>• Gutters and downspouts</li>
                <li>• Signs of leaks or damage</li>
                <li>• Ventilation systems</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Foundation & Structure</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Foundation cracks and settling</li>
                <li>• Moisture intrusion in slabs</li>
                <li>• Crawl space conditions</li>
                <li>• Framing and structural integrity</li>
                <li>• Seismic safety features</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-door-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Exterior Components</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Siding and stucco condition</li>
                <li>• Paint and weatherproofing</li>
                <li>• Windows and doors</li>
                <li>• Drainage systems</li>
                <li>• UV damage assessment</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-drop-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Plumbing Systems</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Water pressure testing</li>
                <li>• Leak detection</li>
                <li>• Drainage evaluation</li>
                <li>• Water heater function</li>
                <li>• Water conservation compliance</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Electrical Systems</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Wiring and outlets</li>
                <li>• Electrical panels</li>
                <li>• GFCI protection</li>
                <li>• Grounding systems</li>
                <li>• Safety compliance</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-temp-cold-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">HVAC Systems</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Heating system performance</li>
                <li>• Cooling system efficiency</li>
                <li>• Ventilation adequacy</li>
                <li>• Ductwork inspection</li>
                <li>• Filter and maintenance</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Interior Components</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Walls and ceilings</li>
                <li>• Flooring conditions</li>
                <li>• Doors and windows</li>
                <li>• Interior safety features</li>
                <li>• Built-in fixtures</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-wifi-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Attic & Insulation</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Ventilation systems</li>
                <li>• Insulation adequacy</li>
                <li>• Structural components</li>
                <li>• Pest evidence</li>
                <li>• Energy efficiency</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-tools-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Appliances</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Built-in appliance operation</li>
                <li>• Safety features</li>
                <li>• Installation quality</li>
                <li>• Basic functionality</li>
                <li>• Code compliance</li>
              </ul>
            </div>
          </div>

          {/* Southern California Specific Factors */}
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              🌡️ Southern California–Specific Factors
            </h3>
            <p className="text-lg text-gray-600 mb-8 text-center max-w-3xl mx-auto">
              Due to our region's unique climate and geology, our inspectors pay special attention to these critical areas:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-earthquake-line text-red-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Seismic Safety</h4>
                <p className="text-gray-600 text-sm">Foundation stability and water heater strapping compliance for earthquake preparedness.</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-sun-line text-orange-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Sun & Heat Damage</h4>
                <p className="text-gray-600 text-sm">Roof wear, UV deterioration, and stucco cracking from intense California sun exposure.</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-bug-line text-yellow-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Termite Activity</h4>
                <p className="text-gray-600 text-sm">Wood-destroying organisms are common; separate termite inspection often required.</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-water-flash-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Water Conservation</h4>
                <p className="text-gray-600 text-sm">Compliance with drought-related plumbing fixture requirements and efficiency standards.</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-water-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Drainage Systems</h4>
                <p className="text-gray-600 text-sm">Grading and runoff management to prevent flooding during rare heavy rains.</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-purple-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Regional Compliance</h4>
                <p className="text-gray-600 text-sm">Local building codes and environmental regulations specific to Southern California.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Receive Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              📋 What You Receive
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive documentation and professional support to help you make informed decisions about your property.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Detailed Written Report</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive digital or PDF report summarizing all findings with clear explanations and professional recommendations.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Digital delivery within 24-48 hours</li>
                <li>• Easy-to understand format</li>
                <li>• Priority classification system</li>
                <li>• Maintenance recommendations</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-camera-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">High-Resolution Photos</h3>
              <p className="text-gray-600 mb-4">
                Detailed photographic documentation of all identified issues and important system components for your records.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Clear, labeled photographs</li>
                <li>• Before and after comparisons</li>
                <li>• Close-up detail shots</li>
                <li>• System component documentation</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Safety Issue Identification</h3>
              <p className="text-gray-600 mb-4">
                Clear identification of safety concerns, major defects, and immediate maintenance needs requiring attention.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Safety hazard alerts</li>
                <li>• Major defect documentation</li>
                <li>• Repair priority rankings</li>
                <li>• Cost estimation guidance</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 shadow-sm">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Fast, Professional Delivery</h3>
                <p className="text-gray-600 mb-6">
                  We understand that timing is critical in real estate transactions. That's why we guarantee report delivery within 24-48 hours of your inspection, giving you the information you need to move forward with confidence.
                </p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">24-48 Hour Delivery</div>
                    <div className="text-sm text-gray-600">Digital report sent directly to your email</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20home%20inspection%20report%20displayed%20on%20tablet%20and%20laptop%20screens%20showing%20detailed%20findings%2C%20photos%2C%20and%20recommendations.%20The%20scene%20shows%20modern%20technology%20being%20used%20for%20home%20inspection%20documentation%20with%20clean%2C%20organized%20digital%20reports.%20The%20background%20is%20bright%20and%20professional%2C%20emphasizing%20the%20quality%20and%20thoroughness%20of%20digital%20inspection%20reporting%20services.&width=600&height=400&seq=report-delivery&orientation=landscape"
                  alt="Digital inspection report delivery"
                  className="rounded-lg shadow-lg object-cover w-full h-64"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Inspection Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our licensed inspectors provide thorough evaluations following California state standards and industry best practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-4-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Pre-Purchase Inspection</h3>
              <p className="text-gray-600 mb-4">
                Complete evaluation of the property's condition before you buy. Identify potential issues and make informed decisions.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Structural assessment</li>
                <li>• Electrical systems</li>
                <li>• Plumbing evaluation</li>
                <li>• HVAC inspection</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Pre-Listing Inspection</h3>
              <p className="text-gray-600 mb-4">
                Get ahead of potential buyer concerns by identifying and addressing issues before listing your property.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Market advantage</li>
                <li>• Faster closing</li>
                <li>• Reduced negotiations</li>
                <li>• Transparent disclosure</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-tools-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Maintenance Inspection</h3>
              <p className="text-gray-600 mb-4">
                Annual property health checks to maintain your investment and prevent costly repairs down the road.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Preventive maintenance</li>
                <li>• Safety assessments</li>
                <li>• System performance</li>
                <li>• Priority recommendations</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-water-flash-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialty Inspections</h3>
              <p className="text-gray-600 mb-4">
                Focused evaluations for specific concerns including pools, foundations, and environmental hazards.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Pool & spa inspection</li>
                <li>• Foundation assessment</li>
                <li>• Mold testing</li>
                <li>• Termite inspection</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Detailed Reporting</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive reports with photos, descriptions, and recommendations delivered within 24 hours.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Digital report delivery</li>
                <li>• High-resolution photos</li>
                <li>• Priority classifications</li>
                <li>• Maintenance tips</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Consultation Support</h3>
              <p className="text-gray-600 mb-4">
                Post-inspection support to help you understand findings and plan next steps with confidence.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Follow-up consultation</li>
                <li>• Contractor referrals</li>
                <li>• Repair prioritization</li>
                <li>• Ongoing support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Home Inspection Services?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                With over 15 years of experience in Southern California real estate, we provide the most thorough and reliable home inspections in the region.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Licensed & Certified</h3>
                    <p className="text-gray-600">CREIA and InterNACHI certified inspectors with continuous education and training.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Same-Day Scheduling</h3>
                    <p className="text-gray-600">Flexible scheduling to meet your timeline with inspections available 7 days a week.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Comprehensive Reports</h3>
                    <p className="text-gray-600">Detailed digital reports with photos and recommendations delivered within 24 hours.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Local Expertise</h3>
                    <p className="text-gray-600">Deep knowledge of Southern California construction methods and common regional issues.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20home%20inspector%20in%20Southern%20California%20examining%20electrical%20panel%20and%20systems%20inside%20a%20modern%20home.%20The%20inspector%20is%20wearing%20professional%20attire%20and%20using%20inspection%20tools.%20The%20scene%20shows%20detailed%20inspection%20work%20with%20natural%20lighting%2C%20clean%20modern%20interior%2C%20and%20professional%20equipment.%20The%20atmosphere%20conveys%20expertise%2C%20thoroughness%2C%20and%20attention%20to%20detail%20in%20home%20inspection%20services.&width=600&height=800&seq=inspector-work&orientation=portrait"
                alt="Professional home inspector at work"
                className="rounded-xl shadow-lg object-cover w-full h-96 lg:h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Inspection Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring every aspect of your property is thoroughly evaluated.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Schedule & Confirm</h3>
              <p className="text-gray-600">
                Book your inspection online or by phone. We'll confirm details and provide preparation guidelines.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Thorough Inspection</h3>
              <p className="text-gray-600">
                Our certified inspector evaluates all major systems and components following industry standards.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Detailed Report</h3>
              <p className="text-gray-600">
                Receive a comprehensive digital report with photos, findings, and recommendations within 24 hours.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Follow-up Support</h3>
              <p className="text-gray-600">
                Get post-inspection consultation and contractor referrals to address any identified issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20home%20inspection%20team%20in%20Southern%20California%20standing%20in%20front%20of%20a%20beautiful%20residential%20property.%20The%20team%20members%20are%20wearing%20professional%20attire%20and%20holding%20inspection%20equipment.%20The%20background%20shows%20a%20well-maintained%20Southern%20California%20home%20with%20palm%20trees%20and%20clear%20blue%20sky.%20The%20scene%20conveys%20professionalism%2C%20expertise%2C%20and%20trustworthiness%20in%20home%20inspection%20services.&width=600&height=400&seq=team-photo&orientation=landscape"
                alt="Our professional inspection team"
                className="rounded-xl shadow-lg object-cover w-full h-80"
              />
            </div>
            
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted Home Inspection Experts
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                For over 15 years, we've been Southern California's premier home inspection service, helping thousands of families make informed real estate decisions.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Over 5,000 successful inspections completed</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">CREIA and InterNACHI certified inspectors</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Comprehensive insurance and bonding</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Same-day scheduling available</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-teal-600 mb-2">15+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-teal-600 mb-2">5K+</div>
                  <div className="text-sm text-gray-600">Inspections</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-teal-600 mb-2">24hr</div>
                  <div className="text-sm text-gray-600">Report Delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by homebuyers, sellers, and real estate professionals across Southern California.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                "Exceptional service! The inspector was thorough, professional, and delivered a comprehensive report that helped us negotiate a better price. Highly recommended!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-teal-600 font-semibold">SM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Sarah Martinez</div>
                  <div className="text-sm text-gray-600">Homebuyer, Irvine</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                "As a real estate agent, I've worked with many inspectors. This team consistently provides the most detailed reports and excellent client communication."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-teal-600 font-semibold">DJ</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">David Johnson</div>
                  <div className="text-sm text-gray-600">Real Estate Agent, Newport Beach</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                "The pre-listing inspection helped us identify and fix issues before putting our home on the market. We sold faster and for a better price!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-teal-600 font-semibold">LW</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Lisa Wong</div>
                  <div className="text-sm text-gray-600">Home Seller, San Diego</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Schedule Your Home Inspection?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            Protect your investment with a professional home inspection. Same-day scheduling available throughout Southern California.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToContact} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Now
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer">
              Call (951) 423-4096
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Contact Us
            </h2>
            <p className="text-xl text-gray-600">
              Get in touch to schedule your inspection or ask any questions.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Get in Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-gray-600">(951) 423-4096</p>
                    <p className="text-sm text-gray-500">Available 7 days a week</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">info@apexhomeinspectionservices.com</p>
                    <p className="text-sm text-gray-500">Response within 2 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Service Area</h4>
                    <p className="text-gray-600">Riverside County, Orange County, Los Angeles County, San Diego County</p>
                    <p className="text-sm text-gray-500">Serving all of Southern California</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Business Hours</h4>
                    <p className="text-gray-600">Monday - Sunday: 7:00 AM - 7:00 PM</p>
                    <p className="text-sm text-gray-500">Emergency inspections available</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Schedule Your Inspection</h3>
              
              <form 
                id="schedule-inspection-form"
                data-readdy-form
                action="https://readdy.ai/api/form/submit/schedule_inspection"
                method="POST"
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const formData = new FormData(form);
                  
                  // Validate required fields
                  const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'propertyAddress', 'inspectionType', 'preferredDate'];
                  const missingFields = requiredFields.filter(field => !formData.get(field));
                  
                  if (missingFields.length > 0) {
                    alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
                    return;
                  }

                  // Check textarea character limit
                  const notes = formData.get('notes') as string;
                  if (notes && notes.length > 500) {
                    alert('Additional notes must be 500 characters or less.');
                    return;
                  }

                  // Submit form
                  fetch(form.action, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Thank you! Your inspection request has been submitted. We will contact you within 2 hours to confirm your appointment.');
                      form.reset();
                    } else {
                      alert('There was an error submitting your request. Please try again or call us directly.');
                    }
                  })
                  .catch(error => {
                    alert('There was an error submitting your request. Please try again or call us directly.');
                  });
                }}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input 
                      type="text" 
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input 
                      type="text" 
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="(951) 423-4096"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Property Address *</label>
                  <input 
                    type="text" 
                    name="propertyAddress"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="123 Main St, City, CA 90210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Inspection Type *</label>
                  <div className="relative">
                    <select 
                      name="inspectionType"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 appearance-none bg-white"
                    >
                      <option value="">Select inspection type</option>
                      <option value="Pre-Purchase Inspection">Pre-Purchase Inspection</option>
                      <option value="Pre-Listing Inspection">Pre-Listing Inspection</option>
                      <option value="Maintenance Inspection">Maintenance Inspection</option>
                      <option value="Specialty Inspection">Specialty Inspection</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                  <input 
                    type="date" 
                    name="preferredDate"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                  <textarea 
                    rows={4}
                    name="notes"
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Any specific concerns or requirements... (500 characters max)"
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Schedule Inspection
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="mb-4">
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center transform rotate-6">
                      <i className="ri-file-list-3-line text-white text-xl"></i>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs font-bold"></i>
                    </div>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-lg font-bold text-white">Apex</span>
                    <span className="text-xs font-semibold text-teal-400 -mt-1">HOME INSPECTION</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Southern California's trusted home inspection service. Licensed, certified, and committed to protecting your investment.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Pre-Purchase Inspection</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Pre-Listing Inspection</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Maintenance Inspection</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Specialty Inspections</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Consultation Support</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>(951) 423-4096</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@apexhomeinspectionservices.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Southern California</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-time-line"></i>
                  <span>7 Days a Week</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2026 Apex Home Inspection Services, LLC. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
              <a href="/terms-of-service" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
